import { DevicesCameraQuery } from '@/graphql/generated/operations'

import { CLOUD_STREAMING_TYPES } from '../constants/constants'
import { CloudCameraOptionIProps } from '../types/types'

/**
 * Maps a DevicesCamera query result to the cloud-camera options the video wall
 * can place. The cell/picker value is the DEVICE id (`node.id`) — that is what
 * CameraFeed($deviceId) consumes, not camera.id.
 *
 * The query is already filtered to cloud streaming types server-side; this keeps
 * a defensive client-side filter so a loosened filter can never leak non-cloud
 * cameras onto the wall.
 */
export const mapCloudCameras = (
  data?: DevicesCameraQuery
): CloudCameraOptionIProps[] => {
  const edges = data?.devices?.edges
  if (!edges) {
    return []
  }

  const cameras = edges.reduce<CloudCameraOptionIProps[]>((options, edge) => {
    const node = edge?.node
    const streamingType = node?.camera?.streamingType
    if (!node?.id || !streamingType) {
      return options
    }
    if (!CLOUD_STREAMING_TYPES.includes(streamingType)) {
      return options
    }
    options.push({
      deviceId: node.id,
      name: node.name ?? '',
      facilityName: node.facility?.name ?? undefined,
      streamingType,
    })
    return options
  }, [])

  // Group by facility name, then order by device name within each facility.
  return cameras.sort(
    (a, b) =>
      (a.facilityName ?? '').localeCompare(b.facilityName ?? '') ||
      a.name.localeCompare(b.name)
  )
}
